import { config } from 'config/config';
import routePaths, { checkRoute, isRouteDefined } from 'config/routes';
import { Dispatch } from 'redux';
import * as api from 'services/api';
import { getUserActiveCampaigns } from 'services/api';
import apiPaths from 'services/apiPaths';
import {
  ChallengeDetail,
  IAction,
  IBrand,
  IBreadCrumb,
  ICategory,
  IChallenges,
  IContentPage,
  IMenu,
  ISalesforceCenter,
  ISpecialty,
  ITag,
  IState,
  ICouponParams,
  ICampaign,
} from 'src/shared/models';
import { IScanError } from 'src/shared/models/scan.model';
import { IDataScan } from 'src/shared/models/scanData.model';
import { appTypes } from 'src/types/appTypes';

const { BASE_URL } = config.API;

/*-------------- APP INTERFACES --------------- */
interface IFetchPublicMenu {
  type: appTypes.APP_FETCH_PUBLIC_MENU;
  payload: {
    publicMenu: IMenu[];
  };
}

interface IFetchShoppingProducts {
  type: appTypes.APP_FETCH_SHOPPING_PRODUCTS;
  payload: {
    shoppingProducts; // TODO: Add interface
  };
}

interface IFetchStates {
  type: appTypes.APP_FETCH_STATES;
  payload: { stateList: IState };
}

interface IFetchSpecialties {
  type: appTypes.APP_FETCH_SPECIALTIES;
  payload: { specialtyList: ISpecialty[] };
}

interface IFetchTags {
  type: appTypes.APP_FETCH_TAGS;
  payload: { tagList: ITag[] };
}

interface IFetchCategories {
  type: appTypes.APP_FETCH_CATEGORIES;
  payload: { categoryList: ICategory[] };
}

interface IFetchBrands {
  type: appTypes.APP_FETCH_BRANDS;
  payload: { brandList: IBrand[] };
}

export interface IUpdateChallenges {
  type: appTypes.APP_UPDATE_CHALLENGES;
  payload: IChallenges;
}

export interface ISetChallenge {
  type: appTypes.APP_SET_CHALLENGE;
  payload: { challenge: ChallengeDetail };
}
interface IFetchEmbracePractices {
  type: appTypes.APP_FETCH_EMBRACE_PRACTICES;
  payload: { embracePractices: ISalesforceCenter[] };
}
interface IFetchLIWWPractices {
  type: appTypes.APP_FETCH_LIWW_PRACTICES;
  payload: { LIWWPractices: ISalesforceCenter[] };
}

interface IFetchUSAPractices {
  type: appTypes.APP_FETCH_USA_PRACTICES;
  payload: { USAPractices: ISalesforceCenter[] };
}

interface ISetPlatformStatus {
  type: appTypes.APP_SET_PLATFORM_STATUS;
  payload: {
    platformStatus: boolean | null;
  };
}

interface ISetSalesforceCenter {
  type: appTypes.APP_SET_SALESFORCE_CENTER;
  payload: {
    salesforceCenter: ISalesforceCenter;
  };
}

interface ISetBreadCrumbs {
  type: appTypes.APP_SET_BREADCRUMBS;
  payload: {
    breadCrumbs: IBreadCrumb[];
  };
}

interface ISetFirstCompletedProfile {
  type: appTypes.APP_SET_COMPLETED_PROFILE_USER;
  payload: { firstCompletedProfile: boolean };
}

interface ISetUserMenu {
  type: appTypes.APP_FETCH_USER_MENU;
  payload: {
    userMenu: null | IMenu[];
  };
}

interface ISetUserHome {
  type: appTypes.APP_FETCH_USER_HOME;
  payload: {
    userHome: null | IContentPage;
  };
}

interface ISetStaticPage {
  type: appTypes.APP_SET_STATIC_PAGE;
  payload: {
    staticPageConfig: {
      hideFooter: boolean | null;
    };
  };
}

interface ISetFooterHeight {
  type: appTypes.APP_SET_FOOTER_HEIGHT;
  payload: {
    isFooterSticky: string;
  };
}

interface ISetActions {
  type: appTypes.APP_SET_ACTIONS;
  payload: {
    actions: IAction[];
  };
}

interface ISetScanError {
  type: appTypes.APP_SET_SCAN_ERROR;
  payload: {
    scanError: IScanError;
  };
}
interface ISetFromScan {
  type: appTypes.APP_SET_FROM_SCAN;
  payload: {
    fromScan: boolean;
  };
}

interface ISetScanData {
  type: appTypes.APP_DATA_SCAN;
  payload: {
    scanData: IDataScan;
  };
}

interface ISetUserCampaigns {
  type: appTypes.APP_SET_CAMPAIGN;
  payload: {
    userCampaigns: ICampaign;
  };
}

interface ISetShowDiscoveryModal {
  type: appTypes.APP_SET_SHOW_DISCOVERY_MODAL;
  payload: {
    showDiscoveryModal: boolean;
  };
}

interface ISetShowSpecialDiscoveryCoupon {
  type: appTypes.APP_SET_SHOW_SPECIAL_DISCOVERY_COUPON;
  payload: {
    showSpecialDiscoveryCoupon: boolean;
  };
}

interface ISetShowStaticPageDiscoveryModal {
  type: appTypes.APP_SET_STATIC_PAGE_DISCOVERY_MODAL;
  payload: {
    showStaticPageDiscoveryModal: boolean;
  };
}

export type AppActionTypes =
  | IFetchBrands
  | IFetchCategories
  | IFetchEmbracePractices
  | IFetchLIWWPractices
  | IFetchPublicMenu
  | IFetchShoppingProducts
  | IFetchSpecialties
  | IFetchTags
  | IFetchStates
  | IFetchUSAPractices
  | ISetBreadCrumbs
  | ISetChallenge
  | ISetFirstCompletedProfile
  | ISetFooterHeight
  | ISetPlatformStatus
  | ISetSalesforceCenter
  | ISetStaticPage
  | ISetUserHome
  | ISetUserMenu
  | IUpdateChallenges
  | ISetActions
  | ISetScanError
  | ISetFromScan
  | ISetScanData
  | ISetUserCampaigns
  | ISetShowDiscoveryModal
  | ISetShowSpecialDiscoveryCoupon
  | ISetShowStaticPageDiscoveryModal;

/*-------------- APP ACTIONS --------------- */
export const fetchPlatformStatus = async (ctx: any) => {
  const reduxStatus = ctx.store.getState().app.platformStatus;
  if (reduxStatus !== null) return;
  let platformStatus;

  try {
    const response = await fetch(
      `${BASE_URL}${apiPaths.CALL.PLATFORM_STATUS}`,
      {
        method: 'GET',
      },
    );

    const result = response.ok ? await response.json() : null;
    platformStatus = result.status;
  } catch (err) {
    platformStatus = null;
    console.error('Fetch platform status:', err);
  }

  ctx.store.dispatch({
    type: appTypes.APP_SET_CHALLENGE,
    payload: { platformStatus },
  });
};

export const fetchPublicMenu = async (ctx: any) => {
  const reduxMenu = ctx.store.getState().app.publicMenu;
  await ctx.store.dispatch({
    type: appTypes.APP_SET_STATIC_PAGE,
    payload: {
      staticPageConfig: {
        hideFooter: false,
      },
    },
  });

  if (reduxMenu === null) {
    try {
      const menu = await fetch(`${BASE_URL}${apiPaths.CALL.PUBLIC_MENU}`, {
        method: 'GET',
      });

      const publicMenu: IMenu[] = menu.ok ? await menu.json() : null;
      const _publicMenu = publicMenu?.map((menu) => {
        return {
          ...menu,
          subMenu: menu.subMenu.map((submenu) => {
            return {
              ...submenu,
              hrefAs: submenu?.route,
              href: checkRoute(submenu.route, routePaths)
                ? submenu.route
                : `/static?page=${submenu.route}`,
            };
          }),
        };
      });

      ctx.store.dispatch({
        type: appTypes.APP_FETCH_PUBLIC_MENU,
        payload: { publicMenu: _publicMenu },
      });
    } catch (err) {
      console.error(err);
    }
  }
};

export const fetchShoppingProducts =
  () => async (dispatch: Dispatch<IFetchShoppingProducts>) => {
    try {
      const response = await api.getDataCall({
        dataPath: '/magento/products/shoppingCart',
        callConfig: {},
      });

      const shoppingProducts = response.data;

      dispatch({
        type: appTypes.APP_FETCH_SHOPPING_PRODUCTS,
        payload: { shoppingProducts },
      });

      return;
    } catch (err) {
      console.error(err);
    }
  };

export const fetchStates = () => async (dispatch: Dispatch<IFetchStates>) => {
  try {
    const response = await api.getDataCall({
      dataPath: apiPaths.MAGENTO.COUNTRIES,
      callConfig: {},
    });

    const stateList: IState = response.data;

    dispatch({
      type: appTypes.APP_FETCH_STATES,
      payload: { stateList },
    });

    return;
  } catch (err) {
    console.error(err);
  }
};

export const fetchSpecialties =
  () => async (dispatch: Dispatch<IFetchSpecialties>) => {
    try {
      const response = await api.getSpecialties({
        dataPath: apiPaths.CALL.SPECIALTY_LIST,
      });

      if (response?.status === 200 && response.data) {
        dispatch({
          type: appTypes.APP_FETCH_SPECIALTIES,
          payload: { specialtyList: response.data },
        });
      }
      return;
    } catch (err) {
      console.error(err);
    }
  };

export const fetchExperienceTags = async (ctx: any) => {
  try {
    const response = await api.getExperienceTags({
      dataPath: apiPaths.CHALLENGES.GET_TAGS_EXPERIENCES,
    });

    if (response?.status === 200 && response.data) {
      ctx.store.dispatch({
        type: appTypes.APP_FETCH_TAGS,
        payload: { tagList: response.data },
      });
    }
    return;
  } catch (err) {
    throw err;
  }
};

export const fetchChallengeTags = async (ctx: any) => {
  try {
    const response = await api.getChallengeTags({
      dataPath: apiPaths.CHALLENGES.GET_TAGS_CHALLENGES,
    });

    if (response?.status === 200 && response.data) {
      ctx.store.dispatch({
        type: appTypes.APP_FETCH_TAGS,
        payload: { tagList: response.data },
      });
    }
    return;
  } catch (err) {
    throw err;
  }
};

export const fetchEmbracePractices =
  (postalCode: string) =>
  async (dispatch: Dispatch<IFetchEmbracePractices>) => {
    try {
      const response = await api.getDataCall({
        dataPath: `${apiPaths.CALL.EMBRACE_PRACTICES}?centerDirectionCP=${postalCode}`,
        callConfig: {},
      });

      const embracePractices: ISalesforceCenter[] = response.data.map(
        (center: ISalesforceCenter) => {
          return {
            ...center,
            name: center.centerName,
            id: center.saleforceID,
          };
        },
      );

      dispatch({
        type: appTypes.APP_FETCH_EMBRACE_PRACTICES,
        payload: { embracePractices },
      });
    } catch (err) {
      console.error(err);
    }
  };

export const fetchLIWWPractices =
  (postalCode: string) => async (dispatch: Dispatch<IFetchLIWWPractices>) => {
    try {
      const response = await api.getDataCall({
        dataPath: `${apiPaths.CALL.LIWW_PRACTICES}?centerDirectionCP=${postalCode}`,
        callConfig: {},
      });

      const LIWWPractices: ISalesforceCenter[] = response.data.map(
        (center: ISalesforceCenter) => {
          return {
            ...center,
            name: center.centerName,
            id: center.centerID,
          };
        },
      );

      dispatch({
        type: appTypes.APP_FETCH_LIWW_PRACTICES,
        payload: { LIWWPractices },
      });
    } catch (err) {
      console.error(err);
    }
  };

export const fetchUSAPractices =
  (postalCode: string) => async (dispatch: Dispatch<IFetchUSAPractices>) => {
    try {
      const response = await api.getDataCall({
        dataPath: `${apiPaths.CALL.USA_PRACTICES}?centerDirectionCP=${postalCode}`,
        callConfig: {},
      });

      const USAPractices: ISalesforceCenter[] = response.data.map(
        (center: ISalesforceCenter) => {
          return {
            ...center,
            name: center.centerName,
            id: center.centerID,
          };
        },
      );

      dispatch({
        type: appTypes.APP_FETCH_USA_PRACTICES,
        payload: { USAPractices },
      });
    } catch (err) {
      console.error(err);
    }
  };

export const fetchBrands = () => async (dispatch: Dispatch<IFetchBrands>) => {
  try {
    const response = await api.getDataCall({
      dataPath: apiPaths.MAGENTO.GET_ISDIN_BRANDS,
      callConfig: {},
    });

    const brandList: IBrand[] = response?.data || [];

    dispatch({
      type: appTypes.APP_FETCH_BRANDS,
      payload: { brandList },
    });
  } catch (err) {
    console.error(err);
  }
};

export const fetchCategories =
  () => async (dispatch: Dispatch<IFetchCategories>) => {
    try {
      const response = await api.getApiCombo(apiPaths.COMBO_IDS.CATEGORIES);

      const categoryList: ICategory[] = response?.data || [];

      dispatch({
        type: appTypes.APP_FETCH_CATEGORIES,
        payload: { categoryList },
      });
    } catch (err) {
      console.error(err);
    }
  };

export const updateChallenges =
  (payload: any) => async (dispatch: Dispatch<IUpdateChallenges>) => {
    try {
      dispatch({
        type: appTypes.APP_UPDATE_CHALLENGES,
        payload,
      });
    } catch (err) {
      console.error(err);
    }
  };

export const setChallenge =
  (challenge: ChallengeDetail) => async (dispatch: Dispatch<ISetChallenge>) => {
    try {
      dispatch({
        type: appTypes.APP_SET_CHALLENGE,
        payload: { challenge },
      });
    } catch (err) {
      console.error(err);
    }
  };

export const setBreadCrumbs =
  (breadCrumbs: IBreadCrumb[]) =>
  async (dispatch: Dispatch<ISetBreadCrumbs>) => {
    try {
      dispatch({
        type: appTypes.APP_SET_BREADCRUMBS,
        payload: { breadCrumbs },
      });
    } catch (err) {
      console.error(err);
    }
  };

export const setStaticPage =
  (staticPage: IContentPage) => async (dispatch: Dispatch<ISetStaticPage>) => {
    try {
      dispatch({
        type: appTypes.APP_SET_STATIC_PAGE,
        payload: {
          staticPageConfig: {
            hideFooter: !staticPage?.footer,
          },
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

// Client side - app actions
export const fetchAppActions =
  (accessToken: string) => async (dispatch: Dispatch<ISetActions>) => {
    const actions = await getAppActions(accessToken);
    if (actions) {
      dispatch({
        type: appTypes.APP_SET_ACTIONS,
        payload: { actions },
      });
    }
  };

export const getAppActions = async (token: string) => {
  const response = await fetch(`${BASE_URL}${apiPaths.CALL.ACTIONS_LIST}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.ok ? await response.json() : null;
};

// Server side - app actions
export const fetchServerAppActions = async (accessToken: string, ctx: any) => {
  const actions = await getAppActions(accessToken);

  if (actions) {
    ctx.store.dispatch({
      type: appTypes.APP_SET_ACTIONS,
      payload: { actions },
    });
  }
};

// Server side - user menu
export const fetchServerUserMenu = async (accessToken: string, ctx: any) => {
  const userMenu = await getUserMenu(accessToken);

  if (userMenu) {
    ctx.store.dispatch({
      type: appTypes.APP_FETCH_USER_MENU,
      payload: { userMenu },
    });
  }
};

// Client side - user menu
export const fetchClientUserMenu =
  (accessToken: string) => async (dispatch: Dispatch<ISetUserMenu>) => {
    const userMenu = await getUserMenu(accessToken);
    if (userMenu)
      dispatch({
        type: appTypes.APP_FETCH_USER_MENU,
        payload: { userMenu },
      });
  };

export const getUserMenu = async (accessToken: string) => {
  const response = await fetch(`${BASE_URL}${apiPaths.CALL.USER_MENU}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const userMenu: IMenu[] = response.ok ? await response.json() : null;

  return userMenu?.map((menu) => {
    return {
      ...menu,
      subMenu: menu.subMenu.map((submenu) => {
        return {
          ...submenu,
          hrefAs: submenu?.route,
          href: isRouteDefined(submenu.route)
            ? submenu.route
            : `/static?page=${submenu.route}`,
        };
      }),
    };
  });
};

export const resetUserMenu = () => (dispatch: Dispatch<ISetUserMenu>) => {
  dispatch({
    type: appTypes.APP_FETCH_USER_MENU,
    payload: { userMenu: null },
  });
};

export const resetUserHome = () => (dispatch: Dispatch<ISetUserHome>) => {
  dispatch({
    type: appTypes.APP_FETCH_USER_HOME,
    payload: { userHome: null },
  });
};

export const setSalesforceCenter =
  (salesforceCenter: ISalesforceCenter) =>
  (dispatch: Dispatch<ISetSalesforceCenter>) => {
    dispatch({
      type: appTypes.APP_SET_SALESFORCE_CENTER,
      payload: { salesforceCenter },
    });
  };

export const setFooterHeight =
  (isFooterSticky: string) => async (dispatch: Dispatch<ISetFooterHeight>) => {
    dispatch({
      type: appTypes.APP_SET_FOOTER_HEIGHT,
      payload: {
        isFooterSticky,
      },
    });
  };

export const setFirstCompletedProfile =
  (firstCompletedProfile: boolean) =>
  (dispatch: Dispatch<ISetFirstCompletedProfile>) => {
    dispatch({
      type: appTypes.APP_SET_COMPLETED_PROFILE_USER,
      payload: {
        firstCompletedProfile,
      },
    });
  };

export const setScanError =
  (scanError: IScanError) => (dispatch: Dispatch<ISetScanError>) => {
    dispatch({
      type: appTypes.APP_SET_SCAN_ERROR,
      payload: { scanError },
    });
  };

export const setFromScan = () => (dispatch: Dispatch<ISetFromScan>) => {
  dispatch({
    type: appTypes.APP_SET_FROM_SCAN,
    payload: { fromScan: true },
  });
};

export const setScanData =
  (scanData: IDataScan) => (dispatch: Dispatch<ISetScanData>) => {
    dispatch({
      type: appTypes.APP_DATA_SCAN,
      payload: { scanData },
    });
  };

export const setUserCampaigns =
  () => async (dispatch: Dispatch<ISetUserCampaigns>) => {
    const campaignUser = await getUserActiveCampaigns();

    dispatch({
      type: appTypes.APP_SET_CAMPAIGN,
      payload: { userCampaigns: campaignUser?.data.campaign },
    });
  };

export const setShowDiscoveryModal =
  (showDiscoveryModal: boolean) =>
  (dispatch: Dispatch<ISetShowDiscoveryModal>) => {
    dispatch({
      type: appTypes.APP_SET_SHOW_DISCOVERY_MODAL,
      payload: { showDiscoveryModal },
    });
  };

export const setShowSpecialDiscoveryCoupon =
  (showSpecialDiscoveryCoupon: boolean) =>
  (dispatch: Dispatch<ISetShowSpecialDiscoveryCoupon>) => {
    dispatch({
      type: appTypes.APP_SET_SHOW_SPECIAL_DISCOVERY_COUPON,
      payload: { showSpecialDiscoveryCoupon },
    });
  };

export const setShowStaticPageDiscoveryModal =
  (showStaticPageDiscoveryModal: boolean) =>
  (dispatch: Dispatch<ISetShowStaticPageDiscoveryModal>) => {
    dispatch({
      type: appTypes.APP_SET_STATIC_PAGE_DISCOVERY_MODAL,
      payload: { showStaticPageDiscoveryModal },
    });
  };
